// Chapter 4 - Measuring Behavior
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 4.
// All content traced to source-pdfs/Chapter-4-Measuring-Behavior-3.pdf

import type { ChapterModule } from "./types";

const chapter04Module: ChapterModule = {
  meta: {
    id: "04",
    number: 4,
    title: "Measuring Behavior",
    subtitle: "Dimensional quantities, event/duration/latency measurement",
    part: "Part 2 - Selecting, Defining, and Measuring Behavior",
    estMinutes: 80,
    bacb: ["C-2", "C-3", "C-4", "C-5", "C-6", "C-7", "C-8", "C-9"],
  },
  curated: true,
  overview: [
    "Chapter 4 turns the operational definitions of Chapter 3 into numbers. Measurement is the process of applying quantitative labels to observed properties of events using a standard set of rules; it is how scientists operationalize empiricism. Without measurement, description, prediction, and control all collapse into guesswork.",
    "The chapter introduces the three dimensional quantities of behavior (repeatability, temporal extent, temporal locus), the two ancillary parameters (topography, magnitude), and the major measurement methods: event recording, time sampling (whole-interval, partial-interval, momentary, PLACHECK), and measurement by permanent product. It closes with a practical decision frame for picking a method based on behavior change goals, ease of detection, setting, and observer resources.",
  ],
  objectives: [
    { id: "C-2", text: "Distinguish among direct, indirect, and product measures of behavior." },
    { id: "C-3", text: "Measure occurrence (count, frequency, rate, percentage)." },
    { id: "C-4", text: "Measure temporal dimensions of behavior (duration, latency, interresponse time)." },
    { id: "C-5", text: "Measure form and strength of behavior (topography, magnitude)." },
    { id: "C-6", text: "Measure trials-to-criterion." },
    { id: "C-7", text: "Design and implement sampling procedures (whole/partial-interval, momentary time sampling)." },
    { id: "C-8", text: "Evaluate the validity and reliability of measurement procedures." },
    { id: "C-9", text: "Select a measurement system to obtain representative data given the dimensions of behavior and the logistics of observing and recording." },
  ],
  coreConcepts: [
    {
      title: "What measurement is for in ABA",
      body: [
        "Measurement applies quantitative labels to observed properties of events using a standard set of rules. It is how scientists operationalize empiricism. Practitioners use measurement to evaluate overall treatment effects (summative) and to make ongoing decisions during treatment (formative). Without frequent measurement, practitioners risk continuing ineffective treatments or discontinuing effective ones based on subjective judgment.",
      ],
      bullets: [
        "Summative evaluation: pre/post comparisons of overall treatment effect.",
        "Formative assessment: frequent measures during treatment that drive decisions.",
        "Measurement also verifies 'evidence-based' claims, surfaces pseudoscience, supports accountability, and protects ethical standards.",
      ],
    },
    {
      title: "Three dimensional quantities of behavior",
      body: [
        "Because behavior occurs within and across time, it has three fundamental dimensional quantities: REPEATABILITY (count), TEMPORAL EXTENT (duration), and TEMPORAL LOCUS (when behavior occurs). All practical measures derive from these three.",
      ],
      bullets: [
        "Repeatability: how many times the behavior occurs (count, rate/frequency, celeration).",
        "Temporal extent: how long an instance lasts (duration).",
        "Temporal locus: when in time the behavior occurs relative to other events (latency, interresponse time).",
      ],
    },
    {
      title: "Repeatability measures",
      body: [
        "Count is a simple tally of occurrences. Rate is count per standard unit of time (counts/minute, counts/hour). Frequency and rate are used interchangeably. Celeration is the acceleration or deceleration in rate per unit time. Percentage is a ratio of same-dimension quantities expressed per 100 opportunities (e.g., percentage correct).",
      ],
      bullets: [
        "Always report the observation period when reporting count.",
        "Rate is preferred over count when observation periods vary.",
        "Trials-to-criterion is the number of response opportunities to reach a predetermined performance level.",
      ],
    },
    {
      title: "Temporal extent and temporal locus measures",
      body: [
        "Duration is the elapsed time from the onset to the offset of an instance of behavior. Response latency is the elapsed time from the onset of a stimulus to the initiation of the response. Interresponse time (IRT) is the elapsed time between two consecutive instances of a response class.",
      ],
      bullets: [
        "Use duration when how-long matters more than how-many (e.g., on-task time, tantrums).",
        "Use latency when responsiveness to an antecedent matters (e.g., compliance to an instruction).",
        "Use IRT when pacing matters (e.g., DRL schedules).",
      ],
    },
    {
      title: "Form and strength: topography and magnitude",
      body: [
        "Topography refers to the physical form or shape of a behavior. Magnitude refers to the force or intensity of a response. Neither is a fundamental dimensional quantity, but both are important for defining and verifying many response classes (e.g., signing a name, throwing a ball with sufficient force).",
      ],
    },
    {
      title: "Event recording vs time sampling",
      body: [
        "Event recording: every instance of the behavior is detected and counted during the observation period. Time sampling: the observer divides the observation period into intervals and scores each interval rather than each instance.",
      ],
      bullets: [
        "Event recording works for discrete, low-to-moderate-rate behaviors with clear onsets/offsets.",
        "Time sampling works for high-rate or continuous behaviors and when continuous observation is impractical.",
      ],
    },
    {
      title: "Three time-sampling variants and PLACHECK",
      body: [
        "Whole-interval recording: score interval YES only if the behavior occurred throughout the entire interval (tends to UNDERestimate). Partial-interval recording: score interval YES if behavior occurred at any point in the interval (tends to OVERestimate). Momentary time sampling: score interval YES only if behavior is occurring at the END of the interval (more neutral, sensitive to ratio of duration to interval length). PLACHECK is momentary time sampling applied to a group.",
      ],
      bullets: [
        "Whole-interval: best for steady, continuous behaviors you want to grow (e.g., on-task).",
        "Partial-interval: best for low-rate problem behaviors you want to shrink.",
        "Momentary time sampling / PLACHECK: best when observers cannot watch continuously.",
      ],
    },
    {
      title: "Measurement by permanent product",
      body: [
        "Permanent products are measurable effects on the environment that persist after the behavior. Pages typed, dishes washed, math problems completed, and litter picked up are all permanent products. The practitioner is freed to do other tasks; measurement can occur at any time.",
      ],
      bullets: [
        "Rule 1: Each occurrence of the target behavior must produce the same permanent product.",
        "Rule 2: The permanent product can be produced only by the target behavior.",
        "Not warranted when moment-to-moment decisions depend on real-time data.",
      ],
    },
    {
      title: "Discrete trial vs free operant",
      body: [
        "A discrete trial is an opportunity arranged by the observer with a defined start, response window, and end (used in DTT). A free operant occurs at any point during the observation period without external pacing (used in natural-environment teaching). The measurement method must match.",
      ],
    },
    {
      title: "Selecting a measurement method",
      body: [
        "Pick a method that fits (a) the behavior change goal and expected direction of change, (b) the ease of detecting occurrences (clear onset/offset?), (c) the environments and times of measurement, and (d) the availability and skills of personnel.",
      ],
      bullets: [
        "Watch for artifact: a datum that does not represent what is actually happening with the behavior.",
        "Pilot the measurement system before scaling it; reactivity and observer drift develop quickly.",
        "Low-tech tools (clickers, golf counters, paper-and-pencil tally) are often best.",
      ],
    },
  ],
  glossary: [
    { term: "Measurement", definition: "Applying quantitative labels to observed properties of events using a standard set of rules.", sourceHint: "Measurement is the process of applying quantitative labels to observed properties of events using a standard set of rules." },
    { term: "Repeatability", definition: "A dimensional quantity referring to the fact that instances of a response class can occur repeatedly through time; underlies count, rate, and celeration.", sourceHint: "behavior has three dimensional quantities: repeatability (i.e., count), temporal extent (i.e., duration), and temporal locus (i.e., when behavior occurs)." },
    { term: "Temporal extent", definition: "A dimensional quantity referring to how long an instance of behavior occupies time; measured by duration.", sourceHint: "temporal extent (i.e., duration)." },
    { term: "Temporal locus", definition: "A dimensional quantity referring to when in time a behavior occurs; underlies latency and interresponse time.", sourceHint: "temporal locus (i.e., when behavior occurs)." },
    { term: "Count", definition: "A simple tally of the number of occurrences of a behavior.", sourceHint: "Count is a simple tally of the number of occurrences of a behavior." },
    { term: "Rate (frequency)", definition: "A ratio of count per observation period, expressed per standard unit of time.", sourceHint: "Rate is a ratio of count per observation period; it is expressed as count per standard unit of time." },
    { term: "Celeration", definition: "A measure of the change (acceleration or deceleration) in rate of responding per unit of time.", sourceHint: "Celeration is a measure of the change (acceleration or deceleration) in rate of responding per unit of time." },
    { term: "Duration", definition: "The amount of time from the onset to the end of an instance of behavior.", sourceHint: "Duration is the amount of time from the onset to the end point of a behavior." },
    { term: "Latency", definition: "The elapsed time between the onset of a stimulus and the initiation of a subsequent response.", sourceHint: "Response latency is a measure of the elapsed time between the onset of a stimulus and the initiation of a subsequent response." },
    { term: "Interresponse time (IRT)", definition: "The amount of time between two consecutive instances of a response class.", sourceHint: "Interresponse time (IRT) is the amount of time that elapses between two consecutive instances of a response class." },
    { term: "Percentage", definition: "A ratio of same-dimension quantities expressing the proportional quantity per 100 opportunities.", sourceHint: "Percentage, a ratio formed by combining the same dimensional quantities, expresses the proportional quantity of an event in terms of the number of times the event occurred per 100 opportunities." },
    { term: "Trials-to-criterion", definition: "The number of response opportunities needed to achieve a predetermined level of performance.", sourceHint: "Trials-to-criterion is a measure of the number of response opportunities needed to achieve a predetermined level of performance." },
    { term: "Topography", definition: "The physical form or shape of a behavior.", sourceHint: "Topography refers to the physical form or shape of a behavior." },
    { term: "Magnitude", definition: "The force or intensity with which a response is emitted.", sourceHint: "Magnitude refers to the force or intensity with which a response is emitted." },
    { term: "Event recording", definition: "Procedures for detecting and recording the number of times a behavior of interest occurs.", sourceHint: "Event recording encompasses a wide variety of procedures for detecting and recording the number of times a behavior of interest is observed." },
    { term: "Time sampling", definition: "A family of methods for observing and recording behavior during intervals or at specific moments in time.", sourceHint: "Time sampling refers to a variety of methods for observing and recording behavior during intervals or at specific moments in time." },
    { term: "Whole-interval recording", definition: "A time-sampling method in which the observer scores an interval as YES only if the behavior occurred throughout the entire interval.", sourceHint: "Observers using whole-interval recording divide the observation period into a series of equal time intervals. At the end of each interval, they record whether the target behavior occurred throughout the entire interval." },
    { term: "Partial-interval recording", definition: "A time-sampling method in which the observer scores an interval as YES if the behavior occurred at any point during the interval.", sourceHint: "Observers using partial-interval recording divide the observation period into a series of equal time intervals. At the end of each interval, they record whether behavior occurred at any point during the interval." },
    { term: "Momentary time sampling", definition: "A time-sampling method in which the observer scores YES only if the behavior is occurring at the end of the interval.", sourceHint: "Observers using momentary time sampling divide the observation period into a series of time intervals. At the end of each interval, they record whether the target behavior is occurring at that specific moment." },
    { term: "Planned activity check (PLACHECK)", definition: "Momentary time sampling applied to a group: at each interval end, score how many people are engaged in the target behavior.", sourceHint: "Planned activity check (PLACHECK) is a variation of momentary time sampling in which the observer records whether each individual in a group is engaged in the target behavior." },
    { term: "Measurement by permanent product", definition: "Measuring behavior after it has occurred by measuring its effects on the environment.", sourceHint: "Measuring behavior after it has occurred by measuring its effects on the environment is known as measurement by permanent product." },
    { term: "Discrete trial", definition: "An opportunity to respond that has a definite onset and offset arranged by the observer or teacher.", sourceHint: "discrete trial (Key Terms list)." },
    { term: "Free operant", definition: "A behavior the learner can emit at any time without the need to wait for the observer to present a trial.", sourceHint: "free operant (Key Terms list)." },
    { term: "Artifact", definition: "A datum that does not represent the behavior under study (a measurement error).", sourceHint: "artifact (Key Terms list)." },
  ],
  procedures: [
    {
      title: "Choose a measurement method for a target behavior",
      intro: "A practical decision tree built from chapter guidance.",
      steps: [
        { title: "Specify the dimensional quantity that matters", detail: "Is the question 'how often,' 'how long,' or 'when?' Answer this first." },
        { title: "Check onset and offset", detail: "If onset/offset are clear and rate is moderate, event recording works. If not, switch to time sampling or permanent product." },
        { title: "Consider observer load", detail: "If observers cannot watch continuously, prefer momentary time sampling or permanent product." },
        { title: "Match expected direction of change", detail: "Whole-interval for behaviors you want to grow; partial-interval for behaviors you want to shrink (note bias direction)." },
        { title: "Pilot and revise", detail: "Run a brief pilot with two observers; review IOA, observer drift, and reactivity. Refine the system." },
      ],
    },
    {
      title: "Whole-interval, partial-interval, and momentary time sampling",
      intro: "Three interval methods - choose by bias direction and observer load.",
      steps: [
        { title: "Divide the observation period", detail: "Mark equal intervals (often 10-30 s)." },
        { title: "Whole-interval", detail: "Score YES at the end of an interval only if behavior occurred throughout that interval (UNDERestimates true occurrence)." },
        { title: "Partial-interval", detail: "Score YES if behavior occurred at any moment during the interval (OVERestimates true occurrence)." },
        { title: "Momentary time sampling", detail: "At the end of each interval, score YES if behavior is occurring at that exact moment." },
        { title: "Summarize", detail: "Report % of intervals scored YES across the observation period; track this metric across sessions." },
      ],
    },
    {
      title: "Setting up measurement by permanent product",
      intro: "Confirm the two rules before relying on a product measure.",
      steps: [
        { title: "Identify the product", detail: "Pick a measurable, persistent change in the environment (worksheet items completed, dishes washed)." },
        { title: "Confirm Rule 1", detail: "Each occurrence of the target behavior must produce the same permanent product." },
        { title: "Confirm Rule 2", detail: "The permanent product can be produced only by the target behavior (rule out other sources)." },
        { title: "Document the rules", detail: "Note any limits (e.g., partner could complete worksheet; observers should rule that out)." },
        { title: "Decide on real-time need", detail: "If moment-to-moment treatment decisions are needed, permanent product is not enough; add a live measure." },
      ],
    },
  ],
  examples: [
    { scenario: "A teacher counts the number of times a student raises a hand across a 40-minute lesson.",
      application: "Event recording; the dimensional quantity is repeatability. Report as count or rate (count/40 min).",
      sourceHint: "Event recording encompasses a wide variety of procedures for detecting and recording the number of times a behavior of interest is observed." },
    { scenario: "A clinician records how long each tantrum lasts.",
      application: "Duration measurement; the dimensional quantity is temporal extent.",
      sourceHint: "Duration is the amount of time from the onset to the end point of a behavior." },
    { scenario: "A behavior analyst times how many seconds elapse between an instruction and the start of compliance.",
      application: "Latency measurement; the dimensional quantity is temporal locus relative to a stimulus.",
      sourceHint: "Response latency is a measure of the elapsed time between the onset of a stimulus and the initiation of a subsequent response." },
    { scenario: "A researcher running a DRL schedule records the time between consecutive responses.",
      application: "Interresponse time (IRT); useful when pacing is the target.",
      sourceHint: "Interresponse time (IRT) is the amount of time that elapses between two consecutive instances of a response class." },
    { scenario: "An observer divides 20 minutes into 30-second intervals and marks an interval YES only if the student was on-task for the entire interval.",
      application: "Whole-interval recording, suitable for behaviors you want to grow; this method UNDERestimates occurrence.",
      sourceHint: "Observers using whole-interval recording divide the observation period into a series of equal time intervals. At the end of each interval, they record whether the target behavior occurred throughout the entire interval." },
    { scenario: "A teacher counts completed math problems on a worksheet at the end of class.",
      application: "Measurement by permanent product; Rule 1 and Rule 2 must be confirmed (no other student helped, all problems came from the student).",
      sourceHint: "Measuring behavior after it has occurred by measuring its effects on the environment is known as measurement by permanent product." },
  ],
  comparisons: [
    {
      title: "Whole-interval vs Partial-interval vs Momentary time sampling",
      left:  { label: "Whole-interval", points: [
        "Score YES only if behavior occurs THROUGHOUT the interval.",
        "Tends to UNDERestimate occurrence.",
        "Good for behaviors you want to GROW (e.g., on-task).",
      ]},
      right: { label: "Partial-interval / Momentary", points: [
        "Partial-interval: any-point-in-interval; OVERestimates.",
        "Momentary: only at the END of the interval; more neutral.",
        "Partial-interval good for behaviors you want to SHRINK; momentary good when observers cannot watch continuously.",
      ]},
    },
    {
      title: "Event recording vs Time sampling",
      left:  { label: "Event recording", points: [
        "Counts every instance; preserves repeatability.",
        "Requires clear onset/offset.",
        "Best for discrete, low-to-moderate-rate behaviors.",
      ]},
      right: { label: "Time sampling", points: [
        "Scores intervals or moments.",
        "Works for high-rate, continuous, or hard-to-pin-down behaviors.",
        "Lower observer load than continuous event recording.",
      ]},
    },
    {
      title: "Direct vs Indirect vs Product measures",
      left:  { label: "Direct (real-time)", points: [
        "Observer watches behavior live and records.",
        "Captures formative data for decision-making.",
      ]},
      right: { label: "Indirect / Product", points: [
        "Indirect: reports, ratings, interviews (not the behavior itself).",
        "Permanent product: measures the environmental effect AFTER behavior occurs.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Whole-interval and partial-interval recording give the same numbers.",
      reality: "They are biased in opposite directions. Whole-interval UNDERestimates; partial-interval OVERestimates true occurrence.",
      sourceHint: "Whole-interval / partial-interval / momentary time sampling definitions in Summary §22-24.",
    },
    {
      myth: "Topography is one of the three dimensional quantities of behavior.",
      reality: "The three dimensional quantities are repeatability, temporal extent, and temporal locus. Topography and magnitude are quantitative parameters but not fundamental dimensions.",
      sourceHint: "Although form (i.e., topography) and intensity of responding (i.e., magnitude) are not fundamental dimensional quantities of behavior, they are important quantitative parameters.",
    },
    {
      myth: "Permanent products are always the easiest method.",
      reality: "They work only when both rules hold: every instance produces the same product AND the product can be produced only by the target behavior. Without that, the count is meaningless.",
      sourceHint: "Rule 1: Each occurrence of the target behavior must produce the same permanent product. Rule 2: The permanent product can be produced only by the target behavior.",
    },
    {
      myth: "Frequency and rate mean different things.",
      reality: "In ABA, frequency and rate are used interchangeably as count per standard unit of time.",
      sourceHint: "Rate is a ratio of count per observation period; it is expressed as count per standard unit of time.",
    },
    {
      myth: "You only need a post-treatment measure to know if treatment worked.",
      reality: "Practitioners need frequent ongoing measures (formative) plus pre/post measures (summative) to avoid continuing ineffective treatments or stopping effective ones.",
      sourceHint: "Without frequent measures of the behavior targeted for intervention, practitioners may (a) continue an ineffective treatment when no real behavior change occurred or (b) discontinue an effective treatment because subjective judgment detects no improvement.",
    },
  ],
  examChecklist: [
    "Name the three dimensional quantities of behavior and one measure each yields.",
    "Distinguish count, rate, and celeration.",
    "Define latency vs IRT with a one-sentence example each.",
    "Distinguish topography from magnitude.",
    "Compare whole-interval, partial-interval, and momentary time sampling; state which biases each carries.",
    "State both rules for using permanent product measurement.",
    "Distinguish discrete trial from free operant.",
    "Pick a measurement method for a given behavior change goal and defend the choice.",
    "Identify one source of measurement artifact in a sample data set.",
    "Explain the difference between formative assessment and summative evaluation.",
  ],
  flashcardTerms: [
    "Measurement", "Repeatability", "Temporal extent", "Temporal locus",
    "Count", "Rate (frequency)", "Celeration", "Duration", "Latency",
    "Interresponse time (IRT)", "Percentage", "Trials-to-criterion",
    "Topography", "Magnitude",
    "Event recording", "Time sampling",
    "Whole-interval recording", "Partial-interval recording", "Momentary time sampling", "PLACHECK",
    "Measurement by permanent product", "Discrete trial", "Free operant", "Artifact",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which of the following is one of the three FUNDAMENTAL dimensional quantities of behavior?",
      options: [
        { label: "A", text: "Topography" },
        { label: "B", text: "Magnitude" },
        { label: "C", text: "Repeatability" },
        { label: "D", text: "Latency" },
      ],
      answer: "C",
      explanation: "Repeatability is one of the three fundamental dimensional quantities (along with temporal extent and temporal locus). Topography and magnitude are quantitative parameters but not fundamental dimensions.",
      topic: "Dimensional quantities",
      sourceHint: "behavior has three dimensional quantities: repeatability (i.e., count), temporal extent (i.e., duration), and temporal locus (i.e., when behavior occurs).",
    },
    {
      id: "Q2",
      question: "Which method UNDERestimates the true occurrence of behavior?",
      options: [
        { label: "A", text: "Partial-interval recording" },
        { label: "B", text: "Whole-interval recording" },
        { label: "C", text: "Momentary time sampling" },
        { label: "D", text: "Event recording" },
      ],
      answer: "B",
      explanation: "Whole-interval scores YES only when the behavior occurred throughout the ENTIRE interval, so any break in the behavior reduces the score and the method underestimates true occurrence.",
      topic: "Time sampling",
      sourceHint: "Observers using whole-interval recording divide the observation period into a series of equal time intervals. At the end of each interval, they record whether the target behavior occurred throughout the entire interval.",
    },
    {
      id: "Q3",
      question: "A teacher times the seconds between an instruction and the start of compliance. The measure is:",
      options: [
        { label: "A", text: "Duration" },
        { label: "B", text: "Interresponse time" },
        { label: "C", text: "Latency" },
        { label: "D", text: "Celeration" },
      ],
      answer: "C",
      explanation: "Latency is the elapsed time between the onset of a stimulus and the initiation of the response.",
      topic: "Temporal locus",
      sourceHint: "Response latency is a measure of the elapsed time between the onset of a stimulus and the initiation of a subsequent response.",
    },
    {
      id: "Q4",
      question: "Both rules for measurement by permanent product require that:",
      options: [
        { label: "A", text: "The product can be produced only by the target behavior AND every occurrence produces the same product." },
        { label: "B", text: "Observation occurs in real time." },
        { label: "C", text: "The product is countable by a digital device." },
        { label: "D", text: "Inter-observer agreement is at least 90%." },
      ],
      answer: "A",
      explanation: "Rule 1 (each occurrence produces the same product) and Rule 2 (only the target behavior produces the product) must both hold.",
      topic: "Permanent product",
      sourceHint: "Rule 1: Each occurrence of the target behavior must produce the same permanent product. Rule 2: The permanent product can be produced only by the target behavior.",
    },
    {
      id: "Q5",
      question: "PLACHECK is best described as:",
      options: [
        { label: "A", text: "Event recording for groups." },
        { label: "B", text: "Momentary time sampling applied to groups." },
        { label: "C", text: "Permanent product measurement of group work." },
        { label: "D", text: "An ABC recording template." },
      ],
      answer: "B",
      explanation: "PLACHECK is a variation of momentary time sampling in which the observer records, at the end of each interval, whether each individual in a group is engaged in the target behavior.",
      topic: "Group measurement",
      sourceHint: "Planned activity check (PLACHECK) is a variation of momentary time sampling in which the observer records whether each individual in a group is engaged in the target behavior.",
    },
    {
      id: "Q6",
      question: "Trials-to-criterion best measures:",
      options: [
        { label: "A", text: "How long a behavior lasts." },
        { label: "B", text: "How long it takes for behavior to start after a cue." },
        { label: "C", text: "How many opportunities are needed to reach a learning criterion." },
        { label: "D", text: "The percentage of intervals with the behavior." },
      ],
      answer: "C",
      explanation: "Trials-to-criterion is the number of response opportunities needed to achieve a predetermined level of performance.",
      topic: "Trials-to-criterion",
      sourceHint: "Trials-to-criterion is a measure of the number of response opportunities needed to achieve a predetermined level of performance.",
    },
    {
      id: "Q7",
      question: "Which measure changes value as observation periods vary in length?",
      options: [
        { label: "A", text: "Count" },
        { label: "B", text: "Rate" },
        { label: "C", text: "Percentage" },
        { label: "D", text: "Latency" },
      ],
      answer: "A",
      explanation: "Raw count is sensitive to observation-period length. Rate divides by time, making it comparable across periods.",
      topic: "Repeatability",
      sourceHint: "Rate is preferred over count when observation periods vary; always report observation period when reporting count.",
    },
  ],
  reviewPrompts: [
    "Pick a target behavior and decide which dimensional quantity to measure; defend the choice.",
    "Sketch a whole-interval, partial-interval, and momentary recording sheet for the same behavior.",
    "Identify a behavior in your work that could be measured by permanent product; verify both rules.",
    "Explain why frequency and rate are interchangeable but rate is usually preferred.",
    "Generate one example of a measurement artifact and propose a procedural fix.",
    "Distinguish discrete trial from free-operant measurement using a real lesson.",
    "Trace summative vs formative measurement decisions for a recent case.",
  ],
  recap: [
    "Measurement gives ABA its empirical backbone. Behavior has three dimensional quantities (repeatability, temporal extent, temporal locus) and two ancillary parameters (topography, magnitude). Each yields specific measures: count/rate/celeration, duration, latency/IRT, topography, magnitude.",
    "Methods break into event recording, time sampling (whole-interval, partial-interval, momentary, PLACHECK), and measurement by permanent product. Time sampling and permanent product trade real-time precision for observer practicality.",
    "Pick a method by matching dimensional quantity, expected direction of change, ease of detection, setting, and observer skill. Pilot, watch for reactivity and artifact, and iterate until the data are trustworthy.",
  ],
  sourceExcerpts: [
    {
      heading: "Definition of measurement",
      text: "Measurement is the process of applying quantitative labels to observed properties of events using a standard set of rules. Measurement is how scientists operationalize empiricism. Without measurement, all three levels of scientific knowledge - description, prediction, and control - would be relegated to guesswork and subjective opinions.",
    },
    {
      heading: "Three dimensional quantities",
      text: "Because behavior occurs within and across time, it has three dimensional quantities: repeatability (i.e., count), temporal extent (i.e., duration), and temporal locus (i.e., when behavior occurs). These properties, alone and in combination, provide the basic and derivative measures used by applied behavior analysts.",
    },
    {
      heading: "Interval recording methods",
      text: "Observers using whole-interval recording divide the observation period into a series of equal time intervals. At the end of each interval, they record whether the target behavior occurred throughout the entire interval. Partial-interval recording records whether behavior occurred at any point during the interval. Momentary time sampling records whether the target behavior is occurring at the end of each interval.",
    },
    {
      heading: "Permanent product rules",
      text: "Behaviors suitable for measurement with permanent products must meet two rules. Rule 1: Each occurrence of the target behavior must produce the same permanent product. Rule 2: The permanent product can be produced only by the target behavior.",
    },
  ],
};

export default chapter04Module;
